import qrcode from "qrcode-generator";
import { h, ic, toast, clear } from "../dom.ts";
import { show, goHome } from "../main.ts";
import { spinGlobe, getMap } from "../map.ts";
import { catalog, scopeName, KIND_INFO, type Catalog } from "../catalog.ts";
import { prefs } from "../prefs.ts";
import { MpGame, createRoom } from "../mp.ts";

/** Invite links and QR codes always use the public domain, never a preview or deployment URL. */
const SHARE_ORIGIN: string = import.meta.env.VITE_SHARE_ORIGIN || "https://playgeopin.com";
import { setupScreen } from "./setup.ts";
import { playScreen } from "./play.ts";
import { resultsScreen } from "./results.ts";
import type { GameConfig } from "../../../shared/game.ts";

export async function hostRoom() {
  try {
    const code = await createRoom(prefs.name);
    joinRoom(code);
  } catch (e) { toast((e as Error).message); }
}

export async function joinRoom(code: string) {
  const cat = await catalog();
  const game = new MpGame(code, prefs.name || "Player", prefs.color);
  history.replaceState(null, "", `/r/${code}`);
  game.onEvent = t => toast(t);
  game.onKicked = () => { goHome(); toast("The host removed you from the room"); };
  game.onLost = () => { goHome(); toast("Lost the connection to the room"); };

  let screen: "lobby" | "setup" | "play" | "results" | null = null;
  const lobbyRoot = h("div", { class: "lobby" });

  const leave = () => { game.quit(); goHome(); };

  game.onChange(() => {
    const s = game.state!;
    if (s.phase === "lobby") {
      if (screen === "setup") return; // host is editing; the lobby redraws when they save
      if (screen !== "lobby") { screen = "lobby"; spinGlobe(true); getMap().easeTo({ zoom: 1.4, duration: 800 }); show(lobbyRoot); }
      renderLobby(lobbyRoot, game, cat, {
        onChange: () => {
          screen = "setup";
          show(setupScreen({ mode: "room", config: s.config,
            onDone: (cfg: GameConfig) => { game.updateConfig(cfg); screen = null; game.refresh(); },
            onBack: () => { screen = null; game.refresh(); } }));
        },
        onLeave: leave,
      });
    } else if (s.phase === "playing" || s.phase === "reveal") {
      if (screen !== "play") {
        screen = "play";
        playScreen(game, s.config, cat, { onEnd: () => {}, onQuit: () => { screen = null; goHome(); } });
      }
    } else if (s.phase === "ended") {
      if (screen !== "results") {
        screen = "results";
        show(resultsScreen(game, s.config, cat, { players: s.players, me: game.you, isHost: game.isHost(),
          onPlayAgain: () => game.playAgain(), onLeave: leave }));
      }
    }
  });

  show(h("div", { class: "loading" }, `Joining room ${code}…`));
}

function renderLobby(root: HTMLElement, game: MpGame, cat: Catalog, o: { onChange: () => void; onLeave: () => void }) {
  const s = game.state!;
  const host = game.isHost();
  const url = `${SHARE_ORIGIN}/r/${s.code}`;
  const qr = qrcode(0, "M");
  qr.addData(url);
  qr.make();
  const qrBox = h("div", { class: "qr", role: "img", "aria-label": `QR code for ${url}` });
  qrBox.innerHTML = qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true });

  const c = s.config;
  const kinds = c.kinds.map(k => KIND_INFO[k].label).join(", ");
  const seats = Array.from({ length: s.playerCount < 8 ? 8 - s.playerCount : 0 });

  clear(root);
  root.append(h("div", { class: "lobby-inner" },
    h("div", { class: "lobby-head" },
      h("button", { class: "icon-btn", "aria-label": "Leave room", onclick: o.onLeave }, ic("back")),
      h("h1", {}, `${s.players.find(p => p.id === s.hostId)?.name ?? "Host"}’s room`),
      h("span", { class: "status-pill" }, "Waiting for players")),
    h("div", { class: "lobby-grid" },
      h("div", { class: "card invite" },
        h("h2", { class: "section-title", style: "margin:0" }, "Invite friends"),
        h("div", { class: "room-code", "aria-label": `Room code ${s.code.split("").join(" ")}` }, s.code),
        qrBox,
        h("div", { class: "link-box" }, h("span", {}, url.replace(/^https?:\/\//, "")),
          h("button", { class: "btn sm sky", onclick: async () => {
            try { await navigator.clipboard.writeText(url); toast("Link copied"); } catch { toast(url); }
          } }, "Copy link")),
        h("p", { class: "muted", style: "margin:0;font-size:13px" }, "Scan with a phone camera to join. Works great on a classroom screen.")),
      h("div", { class: "card", style: "display:flex;flex-direction:column;gap:14px" },
        h("div", { class: "card-title", style: "margin:0" }, h("h2", {}, "Players"), h("span", { class: "muted" }, `${s.playerCount} of ${s.maxPlayers}`)),
        h("div", { class: "players" },
          s.players.map(p => h("div", { class: "player" },
            h("span", { class: "avatar", style: `background:${p.color}` }, p.name[0]?.toUpperCase() ?? "?"),
            h("b", {}, p.id === game.you ? `${p.name} (you)` : p.name),
            p.id === s.hostId ? h("span", { class: "tag" }, "Host")
              : host ? h("button", { class: "btn sm", "aria-label": `Remove ${p.name}`, onclick: () => { if (confirm(`Remove ${p.name} from the room?`)) game.kick(p.id); } }, "Remove") : null)),
          seats.map(() => h("div", { class: "player open" }, "Open seat"))),
        s.playerCount > s.players.length ? h("p", { class: "muted", style: "margin:0;font-size:14px" }, `Showing the latest ${s.players.length}. ${s.playerCount - s.players.length} more in the room.`) : null,
        s.playerCount < 2 ? h("p", { class: "muted", style: "margin:auto 0 0;font-size:14px" }, "Share the code or link. You can also start alone to try it out.") : null),
      h("div", { style: "display:flex;flex-direction:column;gap:14px" },
        h("div", { class: "card" },
          h("div", { class: "card-title" }, h("h2", {}, "Game settings"), host ? h("button", { class: "btn sm", onclick: o.onChange }, "Change") : null),
          kv("Map", scopeName(c.scope, cat)), kv("Find", kinds), kv("Difficulty", c.difficulty[0].toUpperCase() + c.difficulty.slice(1)),
          kv("Rounds", `${c.rounds} · ${c.timer}s each`), kv("Pace", c.mode === "blitz" ? "Blitz (5s reveals)" : "Relaxed (10s reveals)")),
        host
          ? h("button", { class: "btn lime big", style: "margin-top:auto", onclick: () => game.start() }, "Start game")
          : h("div", { class: "card", style: "margin-top:auto;text-align:center" }, h("b", {}, "Waiting for the host to start…"))))));
}

function kv(k: string, v: string) { return h("div", { class: "kv" }, h("span", {}, k), h("span", {}, v)); }
